import { action, computed, makeObservable, observable } from 'mobx'
import { Task } from 'mobx-task'
import { required, validationContext } from 'validx'
import {
  JobTypeOption,
  LocationOption,
  PrimarySoftwareOption,
  SpecialtyOption,
  SupplementalSoftwareOption,
} from 'admin/staffing/dtos/payloads'
import {
  jobTypeOptions,
  locationOptions,
  primarySoftwareOptions,
  specialtyOptions,
  supplementalSoftwareOptions,
} from 'admin/staffing/dtos/workspace-posting-field-options'
import WorkspacePosting, {
  WorkspacePostingOpportunityType,
} from 'admin/staffing/WorkspacePosting'
import Workspace from 'iam/models/Workspace'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import ModalViewModel from 'infra/ModalViewModelSansDecorators'
import { greaterThanOrEqual, lengthBetween } from 'utils/validation-utils'
import { WorkspacePostingOpportunityTypeDto } from '../../dtos/workspace-posting'

/**
 * Base view model for the workspace posting dialog.
 */
export abstract class WorkspacePostingDialogViewModel extends ModalViewModel {
  /**
   * Location field options.
   */
  readonly locationItems = Object.values(locationOptions) as LocationOption[]

  /**
   * Primary software field options.
   */
  readonly primarySoftwareItems = Object.values(
    primarySoftwareOptions,
  ) as PrimarySoftwareOption[]

  /**
   * Supplemental software field options.
   */
  readonly supplementalSoftwareItems = Object.values(
    supplementalSoftwareOptions,
  ) as SupplementalSoftwareOption[]

  /**
   * Specialties field options.
   */
  readonly specialtyItems = Object.values(specialtyOptions) as SpecialtyOption[]

  /**
   * Type of work field options.
   */
  readonly jobTypeItems = Object.values(jobTypeOptions) as JobTypeOption[]

  /**
   * Current workspace posting, if relevant.
   */
  workspacePosting?: WorkspacePosting = undefined

  /**
   * The workspace this posting belongs to.
   */
  workspace?: Workspace = undefined

  /**
   * The opportunity types for this workspace
   */
  opportunityType: WorkspacePostingOpportunityType =
    WorkspacePostingOpportunityType.Unknown

  /**
   * The location (state) relevant to this posting.
   */
  location?: LocationOption = undefined

  /**
   * The primary software required for this posting.
   */
  primarySoftware?: PrimarySoftwareOption = undefined

  /**
   * Whether a backup is required to be maintained for this posting.
   */
  backupRequired: boolean = false

  /**
   * Supplemental software used for this posting.
   */
  supplementalSoftware?: SupplementalSoftwareOption = undefined

  /**
   * Pro specialties relevant for this posting.
   */
  specialties: SpecialtyOption[] = []

  /**
   * The number of pros required for this posting.
   * AKA capacity.
   */
  prosRequired: number = 0

  /**
   * The maximum number of applicants that can be active at any time for this posting.
   */
  maxApplicants: number = 0

  // TODO: Clarify this description later.
  /**
   * The maximum number of workspaces a potential applicant for this posting can be part of.
   */
  maxConcurrentWorkspaces: number = 0

  /**
   * The type of work required for this posting.
   */
  typeOfWork: JobTypeOption[] = []

  /**
   * The description for the posting.
   */
  description = ''

  /**
   * Holds the maximum lengths for each field.
   */
  private readonly fieldLengths = {
    description: { min: 0, max: 1024 },
  } as const

  /**
   * Validation context for workspace postings.
   */
  validation = validationContext(this, {
    workspace: [
      required('A workspace must be selected.'),
      (opts) =>
        opts.value !== Workspace.none || 'A workspace must be selected.',
    ],
    opportunityType: [required('Opportunity type is required')],
    description: [
      lengthBetween(
        this.fieldLengths.description.min,
        this.fieldLengths.description.max,
        `Description must not exceed ${this.fieldLengths.description.max} characters.`,
      ),
    ],
    prosRequired: [greaterThanOrEqual(0, 'Must be zero or greater.')],
    maxApplicants: [greaterThanOrEqual(0, 'Must be zero or greater.')],
    maxConcurrentWorkspaces: [
      greaterThanOrEqual(0, 'Must be zero or greater.'),
    ],
  })

  constructor(
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
  ) {
    super()
    makeObservable(this, {
      workspacePosting: observable,
      workspace: observable,
      opportunityType: observable,
      location: observable,
      primarySoftware: observable,
      backupRequired: observable,
      supplementalSoftware: observable,
      specialties: observable,
      prosRequired: observable,
      maxApplicants: observable,
      maxConcurrentWorkspaces: observable,
      typeOfWork: observable,
      description: observable,
      workspaces: computed,
      isOutsourcing: computed,
      setWorkspace: action.bound,
      setLocation: action.bound,
      setPrimarySoftware: action.bound,
      setBackupRequired: action.bound,
      setSupplementalSoftware: action.bound,
      setSpecialties: action.bound,
      toggleTypeOfWork: action.bound,
      validate: action.bound,
      reset: action.bound,
    })
  }

  /**
   * Returns workspaces in which the user has the ADMIN_STAFFING_MANAGE permission
   * and staffing is enabled in the workspace config.
   */
  get workspaces() {
    return this.getWorkspaceMemberships()
      .filter(
        (mb) =>
          mb.member.hasSomePermissions('ADMIN_STAFFING_MANAGE') &&
          mb.workspace.config.staffing_config?.enabled,
      )
      .map((mb) => {
        return mb.workspace
      })
  }

  /**
   * Returns whether the posting is for an Outsourcing workspace
   */
  get isOutsourcing() {
    return this.opportunityType === WorkspacePostingOpportunityType.Outsourcing
  }

  /**
   * Set workspace field.
   * @param workspace
   */
  setWorkspace = (workspace: Workspace | null) => {
    this.workspace = workspace ?? Workspace.none
  }

  /**
   * Set Opportunity Type field
   * @param opportunityType
   */
  setOpportunityType = (opportunityType?: WorkspacePostingOpportunityType) => {
    this.opportunityType =
      opportunityType || WorkspacePostingOpportunityType.Unknown
  }

  /**
   * Set location field.
   * @param location
   */
  setLocation = (location: LocationOption | null) => {
    this.location = location ?? undefined
  }

  /**
   * Set primary software field.
   * @param primarySoftware
   */
  setPrimarySoftware = (primarySoftware: PrimarySoftwareOption | null) => {
    this.primarySoftware = primarySoftware ?? undefined
  }

  /**
   * Set backup required field.
   * @param backupRequired
   */
  setBackupRequired = (backupRequired: boolean) => {
    this.backupRequired = backupRequired
  }

  /**
   * Set supplemental software field.
   * @param supplementalSoftware
   */
  setSupplementalSoftware = (
    supplementalSoftware: SupplementalSoftwareOption | null,
  ) => {
    this.supplementalSoftware = supplementalSoftware ?? undefined
  }

  /**
   * Set specialties field.
   * @param specialties
   */
  setSpecialties = (specialties: SpecialtyOption[]) => {
    this.specialties = specialties
  }

  /**
   * Toggle type of work stored in typeOfWork array.
   * @param typeOfWork
   */
  toggleTypeOfWork = (typeOfWork: JobTypeOption) => {
    if (this.typeOfWork.includes(typeOfWork)) {
      const index = this.typeOfWork.findIndex((type) => type === typeOfWork)
      this.typeOfWork.splice(index, 1)
    } else {
      this.typeOfWork.push(typeOfWork)
    }
  }

  /**
   * Closes and resets workspace posting dialog.
   */
  close() {
    this.showing = false
    this.reset()
  }

  /**
   * Validates workspace posting dialog fields and returns whether validation passes.
   */
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Clears workspace posting dialog state.
   */
  reset() {
    this.workspacePosting = undefined
    this.workspace = Workspace.none
    this.location = undefined
    this.primarySoftware = undefined
    this.backupRequired = false
    this.supplementalSoftware = undefined
    this.specialties = []
    this.prosRequired = 0
    this.maxApplicants = 0
    this.maxConcurrentWorkspaces = 0
    this.typeOfWork = []
    this.description = ''
    this.opportunityType = WorkspacePostingOpportunityType.Unknown

    this.validation.reset()
  }

  abstract submit: Task<[], Promise<void>>

  mapOpportunityTypeToTitle(opType?: WorkspacePostingOpportunityType) {
    switch (opType) {
      case WorkspacePostingOpportunityType.SignAndFile:
        return 'Sign and File'
      case WorkspacePostingOpportunityType.Outsourcing:
        return 'Outsourcing'
      default:
        return 'Select an Opportunity Type'
    }
  }

  mapOpportunityTypeToDto(
    opType: WorkspacePostingOpportunityType,
  ): WorkspacePostingOpportunityTypeDto {
    switch (opType) {
      case WorkspacePostingOpportunityType.Outsourcing:
        return WorkspacePostingOpportunityTypeDto.Outsourcing
      case WorkspacePostingOpportunityType.SignAndFile:
        return WorkspacePostingOpportunityTypeDto.SignAndFile
      default:
        return WorkspacePostingOpportunityTypeDto.Unknown
    }
  }
}
